/**
 * Deep-link URL builder.
 *
 * Every "Open in mobile app" surface in journey-web constructs its URL
 * through this helper — single source of truth for:
 *   - base host (`citizen.tonnie.ai` / `staging.citizen.tonnie.ai`)
 *   - path prefix (`/l/`)
 *   - param encoding (strips `undefined`, URL-encodes values)
 *
 * Two flavours:
 *   - `buildDeepLink(...)` → returns the Universal Link URL (HTTPS).
 *     This is the canonical form we want in QR codes, email / SMS
 *     shares, and direct clicks on mobile.
 *   - `buildCustomSchemeLink(...)` → returns `tonniecitizen://...`.
 *     Kept as a fallback transport for in-app mobile flows (e.g. a
 *     button inside the mobile app itself referencing a local deep
 *     link). NOT recommended for web surfaces — the OS may block it
 *     depending on the browser context.
 *
 * The `VITE_MOBILE_DEEP_LINK_BASE` env var overrides the base host so
 * preview deploys and E2E can point at a different host without
 * code changes. Default is `https://citizen.tonnie.ai`.
 */

import type { DeepLinkAction, DeepLinkParams } from './actions';
import { runtimeEnv } from '@/core/config/runtime-env';

const DEFAULT_HOST = 'https://citizen.tonnie.ai';
const CUSTOM_SCHEME = 'tonniecitizen://';
const PATH_PREFIX = '/l';

function resolveBase(): string {
  const raw = runtimeEnv.get('VITE_MOBILE_DEEP_LINK_BASE');
  const host = (raw ?? DEFAULT_HOST).replace(/\/$/, '');
  // Protect against misconfig: the env var must be a fully-qualified
  // HTTPS URL. A bare host would produce `/l/<action>` with no
  // protocol, which QR codes would render as unclickable text.
  if (!/^https?:\/\//.test(host)) {
    console.warn(
      `[deep-links] VITE_MOBILE_DEEP_LINK_BASE is not a URL (got "${host}"). ` +
        `Falling back to ${DEFAULT_HOST}.`
    );
    return DEFAULT_HOST;
  }
  return host;
}

/**
 * Strip `undefined` / empty values from `DeepLinkParams` and return
 * a `URLSearchParams` carrying the rest. Stringifies non-string
 * values defensively (we accept `[extra: string]: string | undefined`
 * but callers sometimes pass numbers / booleans).
 */
function toSearchParams(params: DeepLinkParams | undefined): URLSearchParams {
  const sp = new URLSearchParams();
  if (!params) return sp;
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    sp.set(k, String(v));
  }
  return sp;
}

/**
 * Build the canonical Universal Link URL. This is what QR codes,
 * clickable "Open in app" buttons on mobile, and email/SMS shares
 * should use.
 *
 * Example:
 *   buildDeepLink('care/checkpoint', { entityId: 'abc' })
 *   → 'https://citizen.tonnie.ai/l/care/checkpoint?entityId=abc'
 */
export function buildDeepLink(action: DeepLinkAction | string, params?: DeepLinkParams): string {
  const base = resolveBase();
  const url = new URL(`${base}${PATH_PREFIX}/${action}`);
  // URL.searchParams merges cleanly — setting each key preserves the
  // `action` path pieces (which we already embedded above).
  for (const [k, v] of toSearchParams(params)) {
    url.searchParams.set(k, v);
  }
  return url.toString();
}

/**
 * Legacy custom-scheme URL. Retained for contexts where the consumer
 * is already inside the mobile app and wants a deterministic internal
 * handoff. Avoid in new web code.
 *
 * Example:
 *   buildCustomSchemeLink('care/checkpoint', { entityId: 'abc' })
 *   → 'tonniecitizen://care/checkpoint?entityId=abc'
 */
export function buildCustomSchemeLink(
  action: DeepLinkAction | string,
  params?: DeepLinkParams
): string {
  const sp = toSearchParams(params).toString();
  return `${CUSTOM_SCHEME}${action}${sp ? `?${sp}` : ''}`;
}

/**
 * Utility: can this user agent open a mobile app directly? Used by
 * `OpenInAppButton` to decide between "open link" (mobile) and
 * "show QR dialog" (desktop).
 */
export function isMobileUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Utility: which store URL do we send the user to when the app
 * isn't installed? Read by `OpenInAppButton` + the landing page.
 */
export function getStoreUrl(): string {
  if (typeof navigator === 'undefined') {
    return (
      runtimeEnv.get('VITE_CITIZEN_APP_STORE_URL') ||
      'https://apps.apple.com/pt/app/tonnie-citizen/id6778956275?l=en-GB'
    );
  }
  if (/Android/i.test(navigator.userAgent)) {
    return (
      runtimeEnv.get('VITE_CITIZEN_PLAY_STORE_URL') ||
      'https://play.google.com/store/apps/details?id=ai.tonnie.citizen'
    );
  }
  return (
    runtimeEnv.get('VITE_CITIZEN_APP_STORE_URL') ||
    'https://apps.apple.com/pt/app/tonnie-citizen/id6778956275?l=en-GB'
  );
}
